import React, { useEffect, useState, useCallback } from 'react';
import { getConfigurations, updateConfiguration } from '../../services/api';
import PromoManagement from './PromoManagement';
import PromoBundlingManagement from './PromoBundlingManagement';
import '../../styles/ProductManagement.css';

interface Configuration {
  id: number;
  key: string;
  value: string;
  createdAt: string;
  updatedAt: string;
}

const ProductManagement: React.FC = () => {
  const [configurations, setConfigurations] = useState<Configuration[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [defaultPrice, setDefaultPrice] = useState<string>('1000');
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [paymentGatewayEnabled, setPaymentGatewayEnabled] = useState<boolean>(false);
  const [updatingPaymentGateway, setUpdatingPaymentGateway] = useState<boolean>(false);

  useEffect(() => {
    fetchConfigurations();
  }, []);

  const fetchConfigurations = async () => {
    try {
      setLoading(true);
      const configs = await getConfigurations();
      setConfigurations(configs);
      
      // Set default price from configuration
      const priceConfig = configs.find((config: Configuration) => config.key === 'DEFAULT_PRICE');
      if (priceConfig) {
        setDefaultPrice(priceConfig.value);
      }
      
      // Set payment gateway status
      const paymentGatewayConfig = configs.find((config: Configuration) => config.key === 'ENABLE_PAYMENT_GATEWAY');
      if (paymentGatewayConfig) {
        setPaymentGatewayEnabled(paymentGatewayConfig.value === 'true');
      }
      
      setError(null);
    } catch (err: any) {
      console.error('Failed to fetch configurations:', err);
      setError(err.response?.data?.message || 'Failed to load configurations');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePrice = async () => {
    if (!defaultPrice || defaultPrice === '') {
      setError('Please enter a valid price');
      return;
    }

    const priceValue = parseInt(defaultPrice);
    if (isNaN(priceValue) || priceValue < 0) {
      setError('Please enter a valid price (0 or greater)');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      setSuccess(null);
      
      // Update the price
      await updateConfiguration('DEFAULT_PRICE', defaultPrice);
      
      // If price is 0, also disable payment gateway
      if (priceValue === 0 && paymentGatewayEnabled) {
        await updateConfiguration('ENABLE_PAYMENT_GATEWAY', 'false');
        setSuccess('Price updated to 0. Payment gateway has been automatically disabled.');
      } else {
        setSuccess('Price updated successfully');
      }
      
      setIsEditing(false);
      
      // Refresh configurations
      await fetchConfigurations();
    } catch (err: any) {
      console.error('Failed to update price:', err);
      setError(err.response?.data?.message || 'Failed to update price');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTogglePaymentGateway = async () => {
    try {
      setUpdatingPaymentGateway(true);
      setError(null);
      setSuccess(null);
      
      const newValue = !paymentGatewayEnabled;
      
      // If trying to enable payment gateway but the price is 0, show error and prevent toggle
      if (newValue && parseInt(defaultPrice) === 0) {
        setError("Cannot enable payment gateway when price is set to 0. Please set a price greater than 0 first.");
        setUpdatingPaymentGateway(false);
        return;
      }
      
      await updateConfiguration('ENABLE_PAYMENT_GATEWAY', newValue.toString());
      
      setPaymentGatewayEnabled(newValue);
      setSuccess(`Payment gateway ${newValue ? 'enabled' : 'disabled'} successfully`);
      
      // Refresh configurations
      await fetchConfigurations();
    } catch (err: any) {
      console.error('Failed to update payment gateway status:', err);
      setError(err.response?.data?.message || 'Failed to update payment gateway status');
    } finally {
      setUpdatingPaymentGateway(false);
    }
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setDefaultPrice(value);
  };

  const formatPrice = (price: string): string => {
    return `Rp ${parseInt(price || '0').toLocaleString('id-ID')}`;
  };

  if (loading && configurations.length === 0) {
    return (
      <div className="product-management">
        <h2>Product Management</h2>
        <div className="loading-indicator">Loading configurations...</div>
      </div>
    );
  }

  return (
    <div className="product-management-container">
      <div className="product-management">
        <h2>Product Management</h2>
        
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}
        
        <div className="configuration-section">
          <h3>Video Pricing</h3>
          
          <div className="price-configuration">
            {!isEditing ? (
              <>
                <div className="price-display">
                  <label>Price per video:</label>
                  <div className="price-value">{formatPrice(defaultPrice)}</div>
                </div>
                
                <button 
                  className="edit-button" 
                  onClick={() => setIsEditing(true)}
                  disabled={loading}
                >
                  Edit Price
                </button>
              </>
            ) : (
              <div className="price-edit">
                <label htmlFor="priceInput">Price per video (IDR):</label>
                <input
                  id="priceInput"
                  type="text"
                  className="price-input"
                  value={defaultPrice}
                  onChange={handlePriceChange}
                  placeholder="Enter price in IDR"
                />
                
                <div className="button-group">
                  <button 
                    className="save-button" 
                    onClick={handleUpdatePrice}
                    disabled={submitting || !defaultPrice || isNaN(parseInt(defaultPrice)) || parseInt(defaultPrice) < 0}
                  >
                    {submitting ? 'Saving...' : 'Save'}
                  </button>
                  
                  <button 
                    className="cancel-button" 
                    onClick={() => {
                      setIsEditing(false);
                      setError(null);
                      // Reset to the original value
                      const priceConfig = configurations.find((config: Configuration) => config.key === 'DEFAULT_PRICE');
                      if (priceConfig) {
                        setDefaultPrice(priceConfig.value);
                      }
                    }}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="configuration-section">
          <h3>Payment Gateway</h3>
          
          <div className="payment-gateway-configuration">
            {parseInt(defaultPrice) === 0 && (
              <div className="warning-message">
                Payment gateway is disabled because the price is set to 0. To enable payment gateway, please set a price greater than 0.
              </div>
            )}
            <div className="toggle-container">
              <label className="toggle-label">
                Enable payment gateway:
                <div className="toggle-switch-container">
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={paymentGatewayEnabled}
                      onChange={handleTogglePaymentGateway}
                      disabled={updatingPaymentGateway || parseInt(defaultPrice) === 0}
                    />
                    <span className="slider round"></span>
                  </label>
                  <span className="toggle-status">
                    {paymentGatewayEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </label>
            </div>
            <p className="setting-description">
              {paymentGatewayEnabled 
                ? 'Users will be required to make a payment to download videos.'
                : 'Users can download videos without payment (free mode).'}
            </p>
          </div>
        </div>
      </div>
      
      {/* Promo Code Management Section */}
      <PromoManagement />
      <PromoBundlingManagement />
    </div>
  );
};

export default ProductManagement; 